/** Rows of the checkouts table and the rules for removing a worktree. Pure. */
import { machineLiveness } from '@orgus/protocol';
import type { Checkout, Machine, MachineOs, Project, Session, Ts } from '@orgus/protocol';
import { formatInt, plural } from '@orgus/ui';

export interface CheckoutRow {
  checkout: Checkout;
  project: Project | undefined;
  machine: Machine | undefined;
  /** The busiest live session whose folder is this checkout (or inside it). */
  session: Session | undefined;
}

const norm = (p: string) => p.replace(/\\/g, '/').replace(/\/+$/, '').toLowerCase();

/** True when `cwd` is `root` or a folder inside it (case-insensitive, either slash). */
export function isInside(cwd: string, root: string): boolean {
  const a = norm(cwd);
  const b = norm(root);
  return a === b || a.startsWith(`${b}/`);
}

const LIVE_RANK: Record<Session['status'], number> = { working: 0, waiting: 1, idle: 2, ended: 3 };

/** True when normalised path `a` is `b` or inside it. */
const within = (a: string, b: string) => a === b || a.startsWith(`${b}/`);

interface Placed<T> {
  item: T;
  path: string;
}

/** Live sessions and checkouts by machine, their paths normalised once. */
function byMachine(checkouts: readonly Checkout[], sessions: readonly Session[]) {
  const live = new Map<string, Placed<Session>[]>();
  const cos = new Map<string, Placed<Checkout>[]>();
  for (const s of sessions) {
    if (s.status === 'ended') continue;
    let l = live.get(s.machineId);
    if (!l) live.set(s.machineId, (l = []));
    l.push({ item: s, path: norm(s.cwd) });
  }
  for (const c of checkouts) {
    let l = cos.get(c.machineId);
    if (!l) cos.set(c.machineId, (l = []));
    l.push({ item: c, path: norm(c.path) });
  }
  return { live, cos };
}

function pickLive(c: Checkout, live: readonly Placed<Session>[], others: readonly Placed<Checkout>[]): Session | undefined {
  const root = norm(c.path);
  const nested = others.filter((o) => o.item.id !== c.id && o.path !== root && within(o.path, root));
  let best: Session | undefined;
  for (const { item: s, path } of live) {
    if (!(s.checkoutId === c.id || (s.checkoutId == null && within(path, root) && !nested.some((n) => within(path, n.path))))) continue;
    if (!best || LIVE_RANK[s.status] - LIVE_RANK[best.status] < 0 || (s.status === best.status && s.lastActivity > best.lastActivity)) best = s;
  }
  return best;
}

/** The live session in a checkout. A session in a nested worktree belongs to that worktree, not the main one. */
export function liveSessionIn(c: Checkout, sessions: Session[], all: Checkout[]): Session | undefined {
  const { live, cos } = byMachine(all, sessions);
  return pickLive(c, live.get(c.machineId) ?? [], cos.get(c.machineId) ?? []);
}

/**
 * One row per checkout. Sessions and checkouts are grouped by machine first: asking every
 * checkout against every session and every other checkout took about 2.8 s with 1,200
 * checkouts and 10,000 sessions (21 Oct 2026), and froze the page as it opened.
 */
export function buildRows(checkouts: Checkout[], sessions: Session[], projects: Record<string, Project>, machines: Record<string, Machine>): CheckoutRow[] {
  const { live, cos } = byMachine(checkouts, sessions);
  return checkouts.map((c) => ({
    checkout: c,
    project: projects[c.projectId],
    machine: machines[c.machineId],
    session: pickLive(c, live.get(c.machineId) ?? [], cos.get(c.machineId) ?? []),
  }));
}

/** Why this worktree cannot be removed right now, or null when it can. */
export function removeBlocker(r: CheckoutRow, now: Ts): string | null {
  const c = r.checkout;
  if (c.kind !== 'worktree') return 'This is the main checkout. Only worktrees can be removed.';
  if (!r.machine) return 'Its machine is no longer registered.';
  if (machineLiveness(r.machine, now) !== 'online') return `${r.machine.name} is not reporting, so it cannot remove anything.`;
  if (c.dirty > 0) return `It has ${plural(c.dirty, 'changed file')}. Commit or discard them first.`;
  if (r.session) return `A session is ${r.session.status === 'working' ? 'working' : r.session.status === 'waiting' ? 'waiting for you' : 'open'} in it. Stop it first.`;
  return null;
}

/** Light check that a path is absolute for the machine's OS. Returns an error message or null. */
export function pathError(path: string, os: MachineOs | undefined): string | null {
  const p = path.trim();
  if (!p) return 'Enter the folder of a git repository.';
  if (os === 'windows') {
    if (!/^[A-Za-z]:[\\/]/.test(p) && !/^\\\\/.test(p)) return 'Use a full Windows path, such as C:\\code\\app.';
  } else if (os) {
    if (!p.startsWith('/') && !p.startsWith('~')) return `Use a full path, such as ${os === 'macos' ? '/Users/you' : '/home/you'}/code/app or ~/code/app.`;
  }
  return null;
}

export function pathPlaceholder(os: MachineOs | undefined): string {
  return os === 'windows' ? 'C:\\code\\my-app' : os === 'macos' ? '/Users/you/code/my-app' : '/home/you/code/my-app';
}

/** The remote as a short host/owner/repo. */
export function shortRemote(url: string | null): string | null {
  if (!url) return null;
  const scp = /^[\w.-]+@([^:/]+):(.+)$/.exec(url.trim());
  const u = scp ? `${scp[1]}/${scp[2]}` : url.replace(/^[a-z+]+:\/\//i, '').replace(/^[^@/]+@/, '');
  return u.replace(/\.git$/i, '');
}

/**
 * Folders that are scratch, not projects: agent worktrees (`.claude/worktrees/`), Orgus dev
 * homes and fixtures (`.orgus-dev/` and the same folder under its older names), dependency folders and
 * temp directories. Suggestions skip them; they can still be added by hand.
 */
const SCRATCH = [
  /\/\.claude\/worktrees\//,
  /\/\.(orgus|airside|flightdeck)-dev\//, // rename:keep: scratch folders made under the old names still exist on disk
  /\/node_modules\//,
  /\/appdata\/local\/temp\//,
  /^\/tmp\//,
  /^\/private\/var\/folders\//,
  /^[a-z]:\/tmp\//,
];

export function isScratchPath(path: string): boolean {
  const p = `${norm(path)}/`;
  return SCRATCH.some((re) => re.test(p));
}

/** The suggestions worth offering: scratch folders and the ones a person hid are left out. */
export function visibleSuggestions<T extends { machineId: string; path: string }>(list: T[], hidden: ReadonlySet<string>): T[] {
  return list.filter((s) => !isScratchPath(s.path) && !hidden.has(`${s.machineId}:${s.path}`));
}

/** The checkouts count beside the table: what the search shows out of all, so it never says 11 next to "No matches". */
export function checkoutCount(shown: number, total: number): string {
  return shown === total ? plural(total, 'checkout') : `${formatInt(shown)} of ${plural(total, 'checkout')}`;
}
